import { Alert, Badge, Button, useToast } from "@/components/ui";
import {
  emailApi,
  emailProblem,
  needsAttention,
  refreshLabel,
  statusPill,
  type EmailItem,
} from "@/lib/mailboxes";
import { formatDateTime, timeAgo } from "@/lib/format";

import { useEmailMutation } from "./actions";

export function StatusBadge({ item }: { item: EmailItem }) {
  const { label, tone } = statusPill(item);
  return <Badge tone={tone}>{label}</Badge>;
}

/** The request's reference, to quote to support, with a button that copies it. */
export function Reference({ value }: { value: string }) {
  const toast = useToast();
  const copy = async () => {
    try {
      if (!navigator.clipboard) throw new Error("no clipboard");
      await navigator.clipboard.writeText(value);
      toast.show({ id: "email-reference", tone: "success", title: "Reference copied" });
    } catch {
      toast.show({ id: "email-reference", tone: "warning", title: "Copy this reference", body: value });
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span>Reference</span>
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{value}</code>
      <button
        type="button"
        onClick={() => void copy()}
        className="text-xs font-semibold text-brand-700 hover:underline focus-visible:underline"
        aria-label={`Copy reference ${value}`}
      >
        Copy
      </button>
    </span>
  );
}

/** Asks the provider again, safely: settles an unconfirmed change by looking it up, or re-reads the status. */
export function RefreshButton({ item, label }: { item: EmailItem; label?: string }) {
  const refresh = useEmailMutation((org, id: string, key) => emailApi.refresh(org, id, key), {
    done: "Status refreshed",
  });
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <Button
        size="sm"
        variant="secondary"
        loading={refresh.isPending}
        requires={item.can_manage ? undefined : "email:manage"}
        onClick={() => refresh.mutate(item.id)}
      >
        {refresh.isPending ? "Checking…" : (label ?? refreshLabel(item))}
      </Button>
      {refresh.isError && (
        <span role="alert" className="text-xs text-red-700">
          {emailProblem(refresh.error)}
        </span>
      )}
    </span>
  );
}

/** What the person is told about an item that has a problem or is not confirmed: why, the reference, what to do. */
export function ItemNotice({ item }: { item: EmailItem }) {
  const waiting = item.dns_ready === false && item.state === "provisioning";
  if (!needsAttention(item) && !waiting) return null;
  const danger = item.state === "failed";
  return (
    <Alert tone={danger ? "danger" : "warning"}>
      <div className="space-y-1.5">
        {item.problem && <p>{item.problem}</p>}
        {item.next_action && <p>{item.next_action}</p>}
        {item.operation && (
          <p className="text-xs opacity-90">
            <Reference value={item.operation.reference} />
          </p>
        )}
        {(item.state === "unknown" || item.state === "failed" || waiting) && (
          <div className="pt-1">
            <RefreshButton item={item} />
          </div>
        )}
      </div>
    </Alert>
  );
}

/** When the provider last confirmed the item, and a plain warning (with Refresh) when that is old (QB-046). */
export function Freshness({ item }: { item: EmailItem }) {
  if (!item.last_confirmed_at) return null;
  return (
    <p className="text-xs text-fg-subtle">
      <span title={formatDateTime(item.last_confirmed_at)}>
        Last confirmed by the email provider {timeAgo(item.last_confirmed_at)}
      </span>
      {item.stale && (
        <span className="ml-2 inline-flex flex-wrap items-center gap-2">
          <span className="font-medium text-amber-800">Status may be out of date</span>
          <RefreshButton item={item} label="Refresh" />
        </span>
      )}
    </p>
  );
}
